import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { PageHeader } from '@/components/shared/page-header'
import { PageSkeleton } from '@/components/shared/page-skeleton'
import { Eye, EyeOff, Trash2, CheckCircle2, Zap, ExternalLink } from 'lucide-react'
import {
  useKlaviyoStatus,
  useSaveKlaviyoApiKey,
  useTestKlaviyoConnection,
  useRemoveKlaviyo,
} from '@/hooks/use-klaviyo'
import { SectionCard } from '@/components/shared/section-card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { EventsTimeline } from '@/components/settings/events-timeline'
import { KlaviyoImportCard } from '@/components/klaviyo-import-card'
import { CleanupDuplicatesCard } from '@/components/cleanup-duplicates-card'

export const Route = createFileRoute('/_app/settings/integrations/klaviyo')({
  component: KlaviyoSettingsPage,
})

function KlaviyoSettingsPage() {
  const { data: status, isLoading } = useKlaviyoStatus()
  const saveApiKey = useSaveKlaviyoApiKey()
  const testConnection = useTestKlaviyoConnection()
  const removeKlaviyo = useRemoveKlaviyo()

  const [apiKey, setApiKey] = useState('')
  const [showApiKey, setShowApiKey] = useState(false)

  if (isLoading) {
    return <PageSkeleton sections={2} />
  }

  const handleSave = async () => {
    if (!apiKey.trim()) return
    await saveApiKey.mutateAsync(apiKey)
    setApiKey('')
  }

  return (
    <div className="space-y-6">
      <PageHeader
        size="sm"
        title="Klaviyo"
        description="Dispare e-mails automáticos para seus indicadores."
        backHref="/settings/integrations"
      />

      <SectionCard
        title="Configuração"
        description="Conecte o Klaviyo para sincronizar eventos automaticamente."
      >
        {status?.enabled ? (
          <div className="space-y-4">
            <Alert className="border-green-200 bg-green-50 dark:bg-green-950/30 dark:border-green-900">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-700 dark:text-green-400">
                Klaviyo está conectado. Eventos serão sincronizados automaticamente.
              </AlertDescription>
            </Alert>

            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <ChonkyButton
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={() => testConnection.mutateAsync()}
                loading={testConnection.isPending}
              >
                <Zap className="h-3.5 w-3.5" />
                Testar Conexão
              </ChonkyButton>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full sm:w-auto text-destructive hover:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Desconectar
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Desconectar Klaviyo?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Os eventos do programa de indicação não serão mais sincronizados. Você pode
                      reconectar a qualquer momento.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => removeKlaviyo.mutateAsync()}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Desconectar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Private API Key</Label>
              <div className="relative">
                <Input
                  type={showApiKey ? 'text' : 'password'}
                  placeholder="pk_xxxxxxxxxxxxxxxxxxxxxxxx"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Encontre em Account → Settings → API Keys no Klaviyo.{' '}
                <a
                  href="https://www.klaviyo.com/settings/account/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  Abrir Klaviyo <ExternalLink className="h-3 w-3" />
                </a>
              </p>
            </div>

            <ChonkyButton
              onClick={handleSave}
              disabled={!apiKey.trim()}
              loading={saveApiKey.isPending}
            >
              Conectar
            </ChonkyButton>
          </div>
        )}
      </SectionCard>

      {status?.enabled && (
        <>
          <Separator />

          <Separator />

          <KlaviyoImportCard />
          <CleanupDuplicatesCard />

          <Separator />

          <div className="space-y-4">
            <div>
              <h4 className="text-base font-medium">Últimos envios para o Klaviyo</h4>
              <p className="text-sm text-muted-foreground">Eventos sincronizados com o Klaviyo.</p>
            </div>
            <EventsTimeline
              filter={{ platform: 'klaviyo', direction: 'out' }}
              limit={10}
              showFilters={false}
            />
          </div>
        </>
      )}
    </div>
  )
}
